import { createContext, PropsWithChildren, Provider, useContext } from 'react';
import { Button, ButtonProps } from '../Button';
import { cn } from '@nexelem/utils';

type ToggleContextType<T extends string = string> = {
  value: T;
  onChange: (value: T) => void;
};

const ToggleContext = createContext<ToggleContextType | null>(null);

const useToggleContext = () => {
  const context = useContext(ToggleContext);
  if (!context) {
    throw new Error('useToggleContext must be used within a ToggleRoot');
  }
  return context;
};

type ToggleRootProps<T extends string = string> = PropsWithChildren<{
  value: T;
  onChange: (value: T) => void;
  className?: string;
}>;

const ToggleRoot = <T extends string = string>({ children, value, onChange, className }: ToggleRootProps<T>) => {
  const Provider = ToggleContext.Provider as Provider<ToggleContextType<T> | null>;
  return (
    <Provider value={{ value, onChange }}>
      <div className={cn('box-border inline-flex gap-1 rounded-md border border-border-primary p-0.75', className)}>
        {children}
      </div>
    </Provider>
  );
};

type ToggleButtonProps = PropsWithChildren<{
  value: string;
  StartIcon?: ButtonProps['StartIcon'];
}>;

const ToggleButton = ({ children, value, StartIcon }: ToggleButtonProps) => {
  const { value: selectedValue, onChange } = useToggleContext();

  const isSelected = selectedValue === value;
  return (
    <Button
      StartIcon={StartIcon}
      variant="text"
      className={cn(
        'box-border h-6 rounded-lg text-s11-sb text-text-secondary uppercase',
        isSelected && 'border border-border-focus-lighter bg-surface-focus-lighter text-text-status-navy-lighter',
      )}
      onClick={() => onChange(value)}
    >
      {children}
    </Button>
  );
};

export const Toggle = {
  Root: ToggleRoot,
  Button: ToggleButton,
};
